import { useState } from "react";
import { orders } from "@/mocks/orders";
import type { Order } from "@/mocks/orders";

interface OneSOrdersTableProps {
  onRowSelect?: (order: Order) => void;
  selectedOrderId?: string | null;
}

export default function OneSOrdersTable({ onRowSelect, selectedOrderId }: OneSOrdersTableProps) {
  const [sortBy, setSortBy] = useState<keyof Order>("date");
  const [sortDesc, setSortDesc] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("Все");

  const sorted = [...orders]
    .filter((o) => {
      const matchText = !filter ||
        o.client.toLowerCase().includes(filter.toLowerCase()) ||
        o.id.toLowerCase().includes(filter.toLowerCase()) ||
        o.organization.toLowerCase().includes(filter.toLowerCase());
      const matchStatus = statusFilter === "Все" || o.status === statusFilter;
      return matchText && matchStatus;
    })
    .sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDesc ? bVal - aVal : aVal - bVal;
      }
      return 0;
    });

  const handleSort = (key: keyof Order) => {
    if (sortBy === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(key);
      setSortDesc(true);
    }
  };

  const formatSum = (sum: number) =>
    new Intl.NumberFormat("ru-RU", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(sum);

  const statuses = ["Все", ...Array.from(new Set(orders.map((o) => o.status)))];

  const columns: { key: keyof Order; label: string; width: string; align?: string }[] = [
    { key: "id", label: "Номер", width: "w-[110px]" },
    { key: "date", label: "Дата", width: "w-[80px]" },
    { key: "amount", label: "Сумма", width: "w-[90px]", align: "right" },
    { key: "currency", label: "Валюта", width: "w-[50px]" },
    { key: "deal", label: "Сделка", width: "w-[100px]" },
    { key: "organization", label: "Организация", width: "w-[120px]" },
    { key: "status", label: "Текущее состояние", width: "w-[130px]" },
    { key: "shipmentDate", label: "Срок выполнения", width: "w-[90px]" },
    { key: "operation", label: "Операция", width: "w-[100px]" },
  ];

  return (
    <div className="flex flex-col h-full" style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      {/* Filters row */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#FFF9E6] border-b border-[#D5CA8A] flex-wrap text-[11px]"
      >
        <span className="text-[#2D2D2D] font-medium">Текущее состояние:</span>
        <select className="px-2 py-[2px] text-[11px] border border-[#D5CA8A] rounded bg-white text-[#2D2D2D] focus:outline-none cursor-pointer"
        >
          <option>Все</option>
        </select>
        <span className="text-[#2D2D2D] font-medium ml-2">Срок выполнения:</span>
        <select className="px-2 py-[2px] text-[11px] border border-[#D5CA8A] rounded bg-white text-[#2D2D2D] focus:outline-none cursor-pointer"
        >
          <option>Все</option>
        </select>
        <span className="text-[#2D2D2D] font-medium ml-2">Приоритет:</span>
        <select className="px-2 py-[2px] text-[11px] border border-[#D5CA8A] rounded bg-white text-[#2D2D2D] focus:outline-none cursor-pointer"
        >
          <option>Все</option>
        </select>
        <span className="text-[#2D2D2D] font-medium ml-2">Менеджер:</span>
        <select className="px-2 py-[2px] text-[11px] border border-[#D5CA8A] rounded bg-white text-[#2D2D2D] focus:outline-none cursor-pointer"
        >
          <option>Все</option>
        </select>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-[#FFF9E6] border-b border-[#D5CA8A] flex-wrap"
      >
        <button className="flex items-center gap-1 px-2 py-[2px] bg-[#FFF9E6] border border-[#D5CA8A] rounded text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer"
        >
          <i className="ri-add-line text-[11px]"></i>
          Создать
        </button>
        <button className="flex items-center gap-1 px-2 py-[2px] bg-[#FFF9E6] border border-[#D5CA8A] rounded text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer"
        >
          <i className="ri-file-copy-line text-[11px]"></i>
        </button>
        <button className="flex items-center gap-1 px-2 py-[2px] bg-[#FFF9E6] border border-[#D5CA8A] rounded text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer"
        >
          <i className="ri-printer-line text-[11px]"></i>
        </button>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <button className="flex items-center gap-1 px-2 py-[2px] bg-[#FFF9E6] border border-[#D5CA8A] rounded text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer"
        >
          <i className="ri-more-fill text-[11px]"></i>
          Действия
        </button>
        <button className="flex items-center gap-1 px-2 py-[2px] bg-[#FFF9E6] border border-[#D5CA8A] rounded text-[11px] text-[#2D2D2D] hover:bg-[#FDF2B5] transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line text-[11px]"></i>
        </button>
        <div className="w-px h-4 bg-[#D5CA8A] mx-1" />
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Поиск..."
          className="px-2 py-[2px] text-[11px] border border-[#D5CA8A] rounded bg-white text-[#2D2D2D] focus:outline-none focus:border-[#FBED9E] w-32"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-[2px] text-[11px] border border-[#D5CA8A] rounded bg-white text-[#2D2D2D] focus:outline-none cursor-pointer"
        >
          {statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="text-[11px] text-[#888888] ml-auto"
        >
          Всего: {sorted.length} из {orders.length}
        </span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto"
      >
        <table className="w-full text-[11px] border-collapse"
        >
          <thead className="sticky top-0 z-10"
          >
            <tr className="bg-[#FBED9E] text-[#2D2D2D]"
            >
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={`${col.width} ${col.align === "right" ? "text-right" : "text-left"} px-2 py-[3px] border border-[#D5CA8A] font-medium cursor-pointer hover:bg-[#FDF2B5] select-none whitespace-nowrap`}
                >
                  <div className="flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}"
                  >
                    {col.label}
                    {sortBy === col.key && (
                      <i className={sortDesc ? "ri-arrow-down-s-line text-[10px]" : "ri-arrow-up-s-line text-[10px]"}
                      ></i>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((order, idx) => (
              <tr
                key={order.id}
                onClick={() => onRowSelect?.(order)}
                className={`cursor-pointer transition-colors ${
                  selectedOrderId === order.id
                    ? "bg-[#FFFDE8]"
                    : idx % 2 === 0
                    ? "bg-white"
                    : "bg-[#FFF9E6]"
                } hover:bg-[#FDF2B5]`}
              >
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D] font-medium"
                >
                  <div className="flex items-center gap-1"
                  >
                    <i className="ri-file-list-2-line text-[#666666] text-[10px]"
                    ></i>
                    {order.id}
                  </div>
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D]"
                >
                  {new Date(order.date).toLocaleDateString("ru-RU")}
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D] text-right font-medium"
                >
                  {formatSum(order.amount)}
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D]"
                >
                  {order.currency}
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D] truncate max-w-[100px]"
                >
                  {order.deal}
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D] truncate max-w-[120px]"
                >
                  {order.organization}
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D] font-medium"
                >
                  {order.status}
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D]"
                >
                  {new Date(order.shipmentDate).toLocaleDateString("ru-RU")}
                </td>
                <td className="px-2 py-[3px] border border-[#E0E0D0] text-[#2D2D2D]"
                >
                  {order.operation}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}